import * as validators from '@vuelidate/validators'
import { helpers } from '@vuelidate/validators'
import dayjs, { Dayjs } from 'dayjs'
import type { MaybeRef } from 'vue'

function isDateLike(value: unknown): value is string | number | Date | Dayjs | null | undefined {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    value === null ||
    value === undefined ||
    value instanceof Date ||
    value instanceof Dayjs
  )
}

function isValidDate(value: unknown, format: string) {
  if (!isDateLike(value)) {
    return false
  }

  return dayjs(value).format(format) === value
}

export const required = helpers.withMessage('This field is required.', validators.required)

export const integer = helpers.withMessage('This field must be an integer.', validators.integer)

export const maxLength = (max: number) =>
  helpers.withMessage(({ $params }) => `This field cannot exceed ${$params.max} characters.`, validators.maxLength(max))

export const integerBetween = (min: MaybeRef<number>, max: MaybeRef<number>) =>
  helpers.withMessage(
    ({ $params }) => {
      return `This field must contain a whole number between ${$params.min} and ${$params.max}.`
    },
    helpers.withParams({ min, max } as { min: MaybeRef<number>; max: MaybeRef<number> }, value => {
      return (
        validators.minValue(min).$validator(value, null, null) &&
        validators.maxValue(max).$validator(value, null, null) &&
        validators.integer.$validator(value, null, null)
      )
    }),
  )

export const numberBetween = (min: number, max: number) =>
  helpers.withMessage(
    ({ $params }) => {
      return `This field must contain a number between ${$params.min} and ${$params.max}.`
    },
    helpers.withParams({ min, max }, value => {
      return (
        validators.minValue(min).$validator(value, null, null) &&
        validators.maxValue(max).$validator(value, null, null) &&
        validators.numeric.$validator(value, null, null)
      )
    }),
  )

export const date = (format: string = 'YYYY-MM-DD') =>
  helpers.withMessage('This field must contain a valid date.', value => {
    return !helpers.req(value) || isValidDate(value, format)
  })

function extractDate(value: string | Date | (() => string | Date)) {
  if (typeof value === 'function') {
    return value()
  }
  return value
}

export const dateAfter = (
  comparator: string | Date | (() => string | Date),
  format: string = 'YYYY-MM-DD',
  overrideComparator?: string,
) =>
  helpers.withMessage(
    ({ $params }) => {
      const extractedComparator = extractDate($params.comparator)
      const pmt = dayjs(extractedComparator)
      if ($params.overrideComparator) {
        return `This field must contain a date after ${$params.overrideComparator}.`
      } else if (pmt.isSame(dayjs(), 'day')) {
        return 'This field must contain a date in the future.'
      } else {
        return `This field must contain a date after ${pmt.format('DD-MMM-YYYY')}.`
      }
    },
    helpers.withParams({ comparator, overrideComparator }, value => {
      // @ts-expect-error This can be anything
      const dateA = dayjs(value)
      const dateB = dayjs(extractDate(comparator))
      return !helpers.req(value) || !isValidDate(value, format) || dateA.isAfter(dateB)
    }),
  )

export const dateBefore = (
  comparator: string | Date | (() => string | Date),
  format: string = 'YYYY-MM-DD',
  overrideComparator?: string,
) =>
  helpers.withMessage(
    ({ $params }) => {
      const extractedComparator = extractDate($params.comparator)
      const pmt = dayjs(extractedComparator)
      if ($params.overrideComparator) {
        return `This field must contain a date before ${$params.overrideComparator}.`
      } else if (pmt.isSame(dayjs(), 'day')) {
        return 'This field must contain a date in the past.'
      } else {
        return `This field must contain a date before ${pmt.format('DD-MMM-YYYY')}.`
      }
    },
    helpers.withParams({ comparator, overrideComparator }, value => {
      // @ts-expect-error This can be anything
      const dateA = dayjs(value)
      const dateB = dayjs(extractDate(comparator))
      return !helpers.req(value) || !isValidDate(value, format) || dateA.isBefore(dateB)
    }),
  )

export const greaterThan = (comparator: () => number | string, overrideComparator?: string) =>
  helpers.withMessage(
    ({ $params }) => {
      if ($params.overrideComparator) {
        return `This field must contain a number > ${$params.overrideComparator}.`
      } else {
        return `This field must contain a number > ${$params.comparator()}.`
      }
    },
    helpers.withParams({ comparator, overrideComparator }, value => {
      return !helpers.req(value) || comparator() === '' || Number(value) > Number(comparator())
    }),
  )
